import { Controller } from "@hotwired/stimulus"
import Choices from "choices.js"

// Choices.js と stimulus の糊付け。
//
// ローカルモード（既定）: 埋め込み済み `<option>` をクライアント側で fuzzy 検索する。
// リモートモード（`remote-url-value` を指定した場合）: 検索入力に応じて
//   指定 URL に AJAX し、返ってきた `[{ id, label }, ...]` で候補を差し替える。
//   数万〜10 万スケールの候補に耐える。
//
// https://github.com/Choices-js/Choices
export default class extends Controller {
  static targets = ["select"]
  static values = {
    params: Object,
    searchPlaceholder: String,
    remoteUrl: String,     // 例: "/investors.json"
    remoteParam: String,   // 例: "investor_search[q]"
    lookupUrl: String,     // 例: "/funds.json" — URL 復元時に id でラベル解決するための JSON エンドポイント
  }

  connect() {
    // Turbo の back ナビゲーションでキャッシュから復元される際、稀に Choices.js の
    // ラッパー DOM が残ったまま再初期化が走ることがある。その場合は先にラッパーを
    // 取り除いて素の <select> 状態に戻してから初期化する。
    this.unwrapIfNeeded()

    // 初回 connect 時点の <option> 集合を data 属性に保存し、turbo:before-cache
    // 直前に再注入できるようにする。Choices.destroy() は未選択 <option> を
    // 戻し切らないことがあり、そのままキャッシュされると Turbo back 復元時に
    // 「すべて」など他の選択肢が消えて、フィルタ解除すらできなくなる。
    if (this.selectTarget.dataset.choicesjsSnapshot === undefined) {
      this.selectTarget.dataset.choicesjsSnapshot = this.selectTarget.innerHTML
    } else {
      // 既存 snapshot がある = Turbo back 復元など。option が欠けていれば戻す。
      this.restoreOptionsFromSnapshot()
    }

    const remote = this.remote()
    this.choices = new Choices(this.selectTarget, {
      allowHTML: false,
      shouldSort: false,
      classNames: { containerOuter: "choices w-full" },
      itemSelectText: "",
      noResultsText: "見つかりませんでした",
      loadingText: "読込中...",
      renderChoiceLimit: remote ? -1 : 30,
      searchResultLimit: remote ? 50 : 10,
      searchChoices: !remote, // リモートモードでは内部検索を使わない
      placeholder: false,
      searchPlaceholderValue: this.searchPlaceholderValue || null,
      // ローカル fuzzy のゆるさ対策。threshold: 0 + ignoreLocation で「部分文字列必須」相当に。
      fuseOptions: { threshold: 0, ignoreLocation: true },
      callbackOnCreateTemplates: () => ({
        containerOuter: (...args) => {
          const div = Choices.defaults.templates.containerOuter.call(this, ...args)
          const ariaInvalid = this.selectTarget.getAttribute("aria-invalid")
          div.setAttribute("aria-invalid", ariaInvalid)
          return div
        },
      }),
      callbackOnInit: function () {
        let container = this.containerOuter.element
        container.addEventListener(
          "focus",
          () => { this.showDropdown() },
          true
        )
      },
      ...this.paramsValue,
    })

    if (remote) {
      this.onSearch = debounce(this.remoteSearch.bind(this), 250)
      this.selectTarget.addEventListener("search", this.onSearch)
    }

    // Turbo がページをキャッシュする直前に Choices を破棄して、
    // 元の <select> + <option selected> 状態でスナップショットを取らせる。
    // こうしないと、戻るボタンで戻った時に Choices.js が二重初期化されて
    // 選択状態が見えなくなる。
    this.beforeCache = this.beforeCache.bind(this)
    document.addEventListener("turbo:before-cache", this.beforeCache)

    // Turbo の cache 復元時は <option selected> が剥がれている場合があるため、
    // URL パラメータから選択値を読み取って Choices に明示的にセットする。
    this.syncFromURL()
  }

  disconnect() {
    document.removeEventListener("turbo:before-cache", this.beforeCache)
    if (this.onSearch) this.selectTarget.removeEventListener("search", this.onSearch)
    this.destroyChoices()
  }

  beforeCache() {
    this.destroyChoices()
    // キャッシュされる DOM に全 <option> を入れ直しておく。これがないと
    // Turbo back で戻った時に「すべて」を含む未選択肢が消えてしまう。
    this.restoreOptionsFromSnapshot()
  }

  // data 属性に保存しておいた初期 <option> 集合で <select> を上書きし、
  // 現在の選択値だけ HTML 属性レベルでマークし直す（IDL property だけだと
  // outerHTML シリアライズ時に selected が落ちて Turbo cache に乗らない）。
  restoreOptionsFromSnapshot() {
    const snapshot = this.selectTarget.dataset.choicesjsSnapshot
    if (snapshot === undefined) return
    const currentValue = this.selectTarget.value
    this.selectTarget.innerHTML = snapshot
    Array.from(this.selectTarget.options).forEach((o) => {
      o.removeAttribute("selected")
      if (o.value === currentValue) {
        o.setAttribute("selected", "selected")
        o.selected = true
      }
    })
  }

  // URL の検索パラメータから現在の <select> 名に対応する値を読み、
  // Choices の選択状態を強制的に同期する。Turbo back の cache 復元で
  // <option selected> が落ちていても URL は生きているのでそれを真とする。
  async syncFromURL() {
    if (!this.choices) return
    const name = this.selectTarget.name
    if (!name) return

    const url = new URL(window.location.href)
    const value = url.searchParams.get(name)
    if (!value) return

    const existing = this.selectTarget.querySelector(`option[value="${CSS.escape(value)}"]`)
    if (existing) {
      this.choices.setChoiceByValue(value)
      return
    }

    // <option> が cache 復元で落ちている場合は、id でラベルを再取得する。
    const lookupUrl = this.lookupUrlValue || this.remoteUrlValue
    if (lookupUrl) {
      await this.lookupAndSetById(lookupUrl, value)
      return
    }

    // どこからも引けない時の最終手段は値をそのまま表示。
    this.choices.setChoices(
      [{ value, label: value, selected: true }],
      "value",
      "label",
      true,
    )
  }

  async lookupAndSetById(lookupUrl, id) {
    const url = new URL(lookupUrl, window.location.origin)
    url.searchParams.append("ids[]", id)
    try {
      const res = await fetch(url, { headers: { Accept: "application/json" } })
      if (!res.ok) throw new Error(`HTTP ${res.status}`)
      const items = await res.json()
      const item = items.find((i) => String(i.id) === String(id))
      if (!item) throw new Error("not found")
      // 既存 choices を残したまま追加して選択する。replace=true にすると
      // 「すべて」など他の選択肢が消えて、フィルタを外せなくなる。
      this.choices.setChoices(
        [{ value: String(item.id), label: item.label }],
        "value",
        "label",
        false,
      )
      this.choices.setChoiceByValue(String(item.id))
    } catch (e) {
      console.error("[choicesjs] id lookup failed", e)
      this.choices.setChoices(
        [{ value: id, label: id }],
        "value",
        "label",
        false,
      )
      this.choices.setChoiceByValue(String(id))
    }
  }

  destroyChoices() {
    if (this.choices) {
      this.choices.destroy()
      this.choices = null
    }
  }

  // 復元時に Choices ラッパーが残っていたら剥がして素の <select> を親に戻す。
  unwrapIfNeeded() {
    const outer = this.selectTarget.closest(".choices")
    if (!outer || outer === this.selectTarget) return

    // Choices は data-choice 属性を <select> に付けるので消す。
    this.selectTarget.removeAttribute("data-choice")
    this.selectTarget.removeAttribute("hidden")
    this.selectTarget.style.display = ""
    outer.parentElement.replaceChild(this.selectTarget, outer)
  }

  remote() {
    return this.hasRemoteUrlValue && this.remoteUrlValue !== ""
  }

  async remoteSearch(event) {
    const q = event.detail && event.detail.value
    if (!q || q.length === 0) return

    const url = new URL(this.remoteUrlValue, window.location.origin)
    url.searchParams.set(this.remoteParamValue, q)
    try {
      const res = await fetch(url, { headers: { Accept: "application/json" } })
      if (!res.ok) return
      const items = await res.json()
      this.choices.setChoices(
        items.map((i) => ({ value: String(i.id), label: i.label })),
        "value",
        "label",
        true,
      )
    } catch (e) {
      console.error("[choicesjs] remote search failed", e)
    }
  }
}

function debounce(fn, ms) {
  let t
  return (...args) => {
    clearTimeout(t)
    t = setTimeout(() => fn(...args), ms)
  }
}
